import { useEffect, useState } from "react";
import { IoMdClose } from "react-icons/io";
import Button from "../custom/Button";
import { authApp } from "../../services/api";
import { DataState } from "../../context/DataProvider";
import { authenticateApp } from "../../utils/auth";
import { authApps } from "../../utils/constants";

const TriggerModalNew = ({
  title,
  desc,
  isOpen,
  onClose,
  appsData,
  setSelectedOptions,
  selectedOptions,
  editOption,
}) => {
  const [localSelectedOption, setLocalSelectedOption] = useState(null);
  const [selectedTriggerEvents, setSelectedTriggerEvents] = useState([]);
  const [formData, setFormData] = useState({});
  const { userGoogleId } = DataState();
  useEffect(() => {
    if (editOption) {
      setLocalSelectedOption(editOption);
  
      // Prepopulate formData for edit mode
      if (editOption.parameters) {
        const initialData = {};
        const updatedParameters = editOption.parameters.map((param) => {
          const matchingParam = appsData
            .find((app) => app.id === editOption.id)
            ?.parameters.find((p) => p.param === param.param);
  
          return {
            param: param.param,
            value: param.value || "",
            label: matchingParam?.label || param.param, // Use label from appsData or fallback to param
          };
        });
        setLocalSelectedOption((prev) => ({
          ...prev,
          parameters: updatedParameters,
        }));
  
        updatedParameters.forEach((param) => {
          initialData[param.param] = param.value;
        });
  
        setFormData(initialData);
      }
  
      if (editOption.selectedTriggerEvents) {
        setSelectedTriggerEvents([...editOption.selectedTriggerEvents]);
      }
    } else {
      setLocalSelectedOption(null);
      setFormData({});
    }
  }, [editOption, appsData]);
  
  const handleOptionSelect = (option) => {
    if (editOption) return; // Prevent switching options during editing
    setLocalSelectedOption(option);
    setSelectedTriggerEvents([]);
    const initialData = {};
    if (option.parameters) {
      option.parameters.forEach((param) => {
        initialData[param.param] = param.value || "";
      });
    }
    setFormData(initialData);
  };

  const handleTriggerEventSelect = (func) => {
    setSelectedTriggerEvents((prev) =>
      prev.includes(func) ? prev.filter((f) => f !== func) : [...prev, func]
    );
  };

  const handleInputChange = (param, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [param]: value,
    }));
  };

  const handleConfirm = async () => {
    if (localSelectedOption) {
      let triggerEventsToSave = selectedTriggerEvents;

      if (localSelectedOption.triggerEvents && selectedTriggerEvents.length === 0) {
        triggerEventsToSave = [...localSelectedOption.triggerEvents];
      }

      const optionToSave = {
        ...localSelectedOption,
        parameters: Object.entries(formData).map(([param, value]) => ({
          param,
          value,
        })),
        selectedTriggerEvents: triggerEventsToSave,
      };

      const updateAuthApps = () => {
        setSelectedOptions((prev) => {
          if (editOption) {
            return prev.map((item) =>
              item.id === editOption.id ? optionToSave : item
            );
          }
          return [...prev, optionToSave];
        });
      };

      if (authApps.includes(localSelectedOption.value)) {
        await authenticateApp(
          localSelectedOption.value,
          userGoogleId,
          closeModal,
          updateAuthApps
        );
      } else {
        updateAuthApps();
        closeModal();
      }
    }
  };

  const closeModal = () => {
    setLocalSelectedOption(null);
    setSelectedTriggerEvents([]);
    setFormData({});
    onClose();
  };

  return (
    <>
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-[110] bg-black bg-opacity-50"
            onClick={closeModal}
          ></div>
          <div className="fixed top-[50%] z-[120] left-[50%] p-8 rounded-lg bg-2 border br-1 shadow-2xl translate-x-[-50%] translate-y-[-50%] flex flex-col w-[864px] max-w-[calc(100vw-2rem)] max-h-[min(680px,calc(100vh-2rem))] py-6 text-p-text min-h-96">
            <div className="flex flex-col gap-6 flex-1 min-h-0">
              <header className="flex items-center justify-between">
                <div className="flex flex-col gap-1">
                  <h2 className="text-lg font-semibold p-text">{title}</h2>
                  <p className="text-sm text-s-text">{desc}</p>
                </div>
                <button
                  onClick={closeModal}
                  type="button"
                  className="rounded-[3px] text-md font-semibold transition-colors duration-150 ease-in-out disabled:cursor-not-allowed text-xl"
                >
                  <IoMdClose />
                </button>
              </header>

              <div className="flex flex-col gap-4 flex-1 min-h-0 overflow-y-auto">
                {/* Display trigger options */}
                {
                // !localSelectedOption?.triggerEvents && 
                (
                  <div className="flex flex-wrap gap-4 mb-4">
                    {appsData.map((item) => (
                      <button
                        type="button"
                        onClick={() => handleOptionSelect(item)}
                        key={item.id}
                        className={`flex border border-zinc-400 rounded-full items-center gap-2 text-md font-semibold transition-colors duration-150 ease-in-out align-middle text-left px-3 py-2 ${
                          localSelectedOption?.id === item.id
                            ? "bg-zinc-200 text-zinc-800"
                            : selectedOptions.some((opt) => opt.id === item.id)
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                        disabled={
                          selectedOptions.some((opt) => opt.id === item.id) &&
                          !editOption
                        || editOption // Disable all buttons during editing
                        }
                      >
                        <div className="bg-zinc-200 rounded-lg p-1">
                          <img
                            alt={item.label}
                            src={item.image}
                            className="h-7 w-7"
                          />
                        </div>
                        <div className="text-s-text text-lg ml-1">{item.label}</div>
                      </button>
                    ))}
                  </div>
                )}
                {/* Show triggerEvents if available */}
                {localSelectedOption?.triggerEvents && (
                  <div className="flex flex-col gap-2">
                    <label className="text-sm ">Select Webhook Trigger Events</label>
                    <div className="flex flex-wrap gap-2">
                      {localSelectedOption.triggerEvents.map((func) => (
                        <button
                          key={func}
                          className={`px-3 py-1.5 rounded-full text-sm text-s-text transition-all duration-200 ${
                            selectedTriggerEvents.includes(func)
                              ? "bg-zinc-100 text-zinc-800 hover:bg-zinc-300"
                              : "bg-zinc-800 text-zinc-100 hover:bg-zinc-700 hover:text-zinc-100"
                          }`}
                          onClick={() => handleTriggerEventSelect(func)}
                        >
                          <span className="text-sm">
                            {func.charAt(0).toUpperCase() +
                              func.slice(1).toLowerCase()}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Render parameters dynamically */}
                {localSelectedOption?.parameters && (
                  <div className="flex flex-col gap-2">
                    {localSelectedOption.parameters.map((param) => (
                      <div key={param.param} className="flex flex-col gap-2">
                        <label className="text-sm">{param.label}</label>
                        <input
                          type="text"
                          value={formData[param.param] || ""}
                          onChange={(e) =>
                            handleInputChange(param.param, e.target.value)
                          }
                          className="p-2 rounded-md bg-1 outline-none"
                        />
                      </div>
                    ))}
                  </div>
                )}

                
              </div>

              {/* Confirm button */}
              {localSelectedOption && (
                <footer className="flex justify-end gap-4">
                  <div className="w-28">
                    <Button onClick={handleConfirm}>Confirm</Button>
                  </div>
                </footer>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default TriggerModalNew;
